<template>
  <HpsfirProDrawer
    v-model:open="visible"
    :title="`授权设备${adminName ? ` - ${adminName}` : ''}`"
    width="860px"
    max-width="calc(100vw - 32px)"
    mobile-height="75vh"
    mobile-max-height="75vh"
    content-class="business-table-drawer"
    body-class="business-table-drawer__body"
  >
    <div class="auth-device-drawer">
      <div class="auth-device-drawer__toolbar">
        <HpsfirInput
          v-model="keyword"
          placeholder="搜索设备备注 / 设备序列号 / 原始序列号"
          class="auth-device-drawer__search"
          :disabled="authorizeAllDevices"
        />
        <span class="auth-device-drawer__summary">
          {{ authorizeAllDevices ? '已授权全部设备（含后续新增）' : `已选 ${selectedDeviceIds.size} / 共 ${filteredDevices.length} 台` }}
        </span>
      </div>

      <label class="auth-device-drawer__all" :class="{ 'auth-device-drawer__all--checked': authorizeAllDevices }">
        <input
          class="auth-device-drawer__checkbox"
          type="checkbox"
          :checked="authorizeAllDevices"
          @change="toggleAuthorizeAll"
        />
        <span class="auth-device-drawer__all-main">
          <span class="auth-device-drawer__title">授权全部设备</span>
          <span class="auth-device-drawer__meta">不保存具体设备明细，当前和后续新增设备都可见，效果与管理员查看设备范围一致。</span>
        </span>
      </label>

      <div class="auth-device-drawer__list" :class="{ 'auth-device-drawer__list--disabled': authorizeAllDevices }">
        <label
          v-for="device in filteredDevices"
          :key="device.id"
          class="auth-device-drawer__item"
          :class="{ 'auth-device-drawer__item--checked': selectedDeviceIds.has(device.id) }"
        >
          <input
            class="auth-device-drawer__checkbox"
            type="checkbox"
            :checked="selectedDeviceIds.has(device.id)"
            :disabled="authorizeAllDevices"
            @change="toggleDevice(device.id)"
          />
          <span class="auth-device-drawer__main">
            <span class="auth-device-drawer__title">{{ device.remark || '未备注设备' }}</span>
            <span class="auth-device-drawer__meta">设备序列号：{{ device.idHex || '-' }}</span>
            <span class="auth-device-drawer__meta">原始序列号：{{ device.serialNumber || '-' }}</span>
          </span>
        </label>
        <div v-if="!filteredDevices.length" class="auth-device-drawer__empty">暂无设备</div>
      </div>
    </div>

    <template #footer>
      <div class="auth-device-drawer__footer">
        <HpsfirButton variant="outline" @click="visible = false">取消</HpsfirButton>
        <HpsfirButton :loading="loading" @click="handleSave">保存授权</HpsfirButton>
      </div>
    </template>
  </HpsfirProDrawer>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import { ElMessage } from 'element-plus';
import { useApi } from '@/hooks/useApi';

const emit = defineEmits(['saved']);

const visible = ref(false);
const loading = ref(false);
const adminId = ref<number>();
const adminName = ref('');
const keyword = ref('');
const devices = ref<any[]>([]);
const selectedDeviceIds = ref(new Set<number>());
const authorizeAllDevices = ref(false);

const filteredDevices = computed(() => {
  const key = keyword.value.trim().toLowerCase();
  if (!key) {
    return devices.value;
  }
  return devices.value.filter((device) => [
    device.remark,
    device.idHex,
    device.serialNumber,
  ].some((value) => String(value || '').toLowerCase()
    .includes(key)));
});

const toggleDevice = (deviceId: number) => {
  if (authorizeAllDevices.value) {
    return;
  }
  const next = new Set(selectedDeviceIds.value);
  if (next.has(deviceId)) {
    next.delete(deviceId);
  } else {
    next.add(deviceId);
  }
  selectedDeviceIds.value = next;
};

const toggleAuthorizeAll = () => {
  authorizeAllDevices.value = !authorizeAllDevices.value;
  if (authorizeAllDevices.value) {
    selectedDeviceIds.value = new Set<number>();
    keyword.value = '';
  }
};

const loadDevices = async() => {
  const res = await useApi().deviceApi.listDeviceDevice({ conditions: {} });
  devices.value = res.data.data || [];
};

const loadAuthorizedDeviceInfo = async(id: number) => {
  const res = await useApi().adminDeviceAuthApi.getAuthorizedDeviceInfo(id);
  authorizeAllDevices.value = Boolean(res.data.data?.authorizeAllDevices);
  selectedDeviceIds.value = new Set((res.data.data?.deviceIds || []) as number[]);
};

const show = async(row: any) => {
  adminId.value = row.id;
  adminName.value = row.name || row.phone || '';
  keyword.value = '';
  authorizeAllDevices.value = false;
  visible.value = true;
  loading.value = true;
  try {
    await Promise.all([
      loadDevices(),
      loadAuthorizedDeviceInfo(row.id),
    ]);
  } finally {
    loading.value = false;
  }
};

const handleSave = async() => {
  if (!adminId.value) {
    ElMessage.error('请选择账号');
    return;
  }
  loading.value = true;
  try {
    await useApi().adminDeviceAuthApi.saveAuthorizedDevices({
      adminId: adminId.value,
      authorizeAllDevices: authorizeAllDevices.value,
      deviceIds: authorizeAllDevices.value ? [] : Array.from(selectedDeviceIds.value),
    });
    ElMessage.success(authorizeAllDevices.value ? '已授权全部设备' : '授权设备保存成功');
    visible.value = false;
    emit('saved');
  } finally {
    loading.value = false;
  }
};

defineExpose({ show });
</script>

<style lang="scss" scoped>
.auth-device-drawer {
  display: flex;
  min-height: 0;
  flex-direction: column;
  gap: 14px;
}

.auth-device-drawer__toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
}

.auth-device-drawer__search {
  flex: 1;
  min-width: 0;
}

.auth-device-drawer__summary {
  color: var(--hpsfir-text-secondary, #64748b);
  font-size: 13px;
  white-space: nowrap;
}

.auth-device-drawer__list {
  display: grid;
  max-height: calc(75vh - 220px);
  overflow: auto;
  gap: 10px;
  padding-right: 4px;
}

.auth-device-drawer__list--disabled {
  opacity: .54;
  pointer-events: none;
}

.auth-device-drawer__all,
.auth-device-drawer__item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  border: 1px solid var(--hpsfir-border-color, #e2e8f0);
  border-radius: 12px;
  background: var(--hpsfir-bg-card, #fff);
  cursor: pointer;
  transition: border-color .18s ease, box-shadow .18s ease, background .18s ease;
}

.auth-device-drawer__all--checked,
.auth-device-drawer__item--checked {
  border-color: var(--hpsfir-color-primary, #16a34a);
  background: color-mix(in srgb, var(--hpsfir-color-primary, #16a34a) 8%, white);
  box-shadow: 0 8px 20px rgba(22, 163, 74, .12);
}

.auth-device-drawer__checkbox {
  margin-top: 4px;
}

.auth-device-drawer__all-main,
.auth-device-drawer__main {
  display: grid;
  min-width: 0;
  gap: 4px;
}

.auth-device-drawer__title {
  color: var(--hpsfir-text-primary, #0f172a);
  font-weight: 600;
}

.auth-device-drawer__meta {
  color: var(--hpsfir-text-secondary, #64748b);
  font-size: 13px;
  overflow-wrap: anywhere;
}

.auth-device-drawer__empty {
  padding: 40px 0;
  color: var(--hpsfir-text-secondary, #64748b);
  text-align: center;
}

.auth-device-drawer__footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

@media (max-width: 768px) {
  .auth-device-drawer__toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .auth-device-drawer__summary {
    white-space: normal;
  }
}
</style>
